import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { ErrorState, Loading, EmptyState } from "../../../components/States";
import { PageHead } from "../../../components/ui";
import { useCreatorContext, DemoNote } from "../ui";
import { dateOf, useT } from "../sprache";
import { loadMeineVertraege, meinStand, standPill } from "./daten";
import { useNurLesen } from "../../../app/nurLesen";
import { ElternEinwilligung } from "./ElternEinwilligung";

/** Deine Verträge: offene zuerst, mit deinem Stand und dem nächsten Schritt (Du-Form, Deutsch und Englisch, Befund 2). */
export function MeineVertraege() {
  const { ws, scope, lang, today } = useCreatorContext();
  const t = useT();
  const q = useQuery({ queryKey: ["creator", scope.talentId, "vertraege"], queryFn: () => loadMeineVertraege(api, scope) });
  const nurLesen = useNurLesen();
  const brands = useQuery({ queryKey: ["creator", scope.talentId, "marken"], queryFn: () => api.select<{ id: string; name: string }>("talent_brands", { columns: "id, name", filters: [["org_id", "eq", scope.orgId]] }) });
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Loading />;
  const rank = { sign: 0, wait_fill: 1, minor: 2, declined: 2, stopped: 2, paused: 2, readonly: 2, signed: 3, guardian: 3, info: 4, done: 4, ended: 5, void: 5 } as const;
  const rows = [...q.data].sort((a, b) => rank[meinStand(a, today, nurLesen)] - rank[meinStand(b, today, nurLesen)] || (b.sent_at ?? "").localeCompare(a.sent_at ?? ""));
  const brand = (id: string | null) => brands.data?.find((b) => b.id === id)?.name ?? "";
  const when = (d: string) => dateOf(d, lang, ws.org.timezone);
  return (
    <main id="main" className="content tk-cr tk-cr-vertraege">
      <PageHead title={t("Deine Verträge", "Your contracts")} eyebrow={t("Dein Creator-Portal", "Your creator portal")}
        text={t(`Verträge mit ${ws.org.name}. Lies jeden Vertrag vollständig, bevor du unterschreibst.`, `Contracts with ${ws.org.name}. Read each contract in full before you sign.`)} />
      {/* CREATOR-R3-2: fehlt die Einwilligung der Eltern (Geburtsdatum erst nach der Einladung eingetragen), wird sie hier nachgeholt */}
      <ElternEinwilligung />
      {rows.length === 0 ? (
        <EmptyState title={t("Noch keine Verträge.", "No contracts yet.")} text={t("Sobald deine Agentur dir einen Vertrag schickt, findest du ihn hier.", "As soon as your agency sends you a contract, you will find it here.")} />
      ) : (
        <div className="stack" data-coco="vertraege-liste">
          {rows.map((c) => {
            const st = meinStand(c, today, nurLesen);
            const p = standPill(st, lang);
            return (
              <Link key={c.id} className="task" to={`/creator/vertraege/${c.id}`}>
                <div>
                  <strong>{c.title}</strong>
                  <p>{[brand(c.brand_id), c.contract_number_label,
                    c.origin === "pdf_upload" ? t("als PDF abgelegt", "filed as PDF") : c.sent_at ? t(`erhalten am ${when(c.sent_at)}`, `received on ${when(c.sent_at)}`) : null,
                    c.completed_at && (st === "done" || st === "ended") ? t(`abgeschlossen am ${when(c.completed_at)}`, `completed on ${when(c.completed_at)}`) : null].filter(Boolean).join(" · ")}</p>
                </div>
                <span className={`pill ${p.tone}`}><span className="pill-zeichen">{p.symbol}</span> {p.label}</span>
              </Link>
            );
          })}
        </div>
      )}
      <DemoNote />
    </main>
  );
}
